import { supabase } from '@/lib/supabase'
import { HOLD_STATUSES, TERMINAL_STATUSES } from '@/pages/field/jobStatusTransition'

/**
 * Schedule-conflict detection for employee + vendor double-booking.
 *
 * Two entry points:
 *   detectConflicts        — async, single-job check. Used by NewJobModal /
 *                            JobDetail at assignment time.
 *   detectConflictsInList  — sync, O(n²) within the loaded list. Used by
 *                            FieldHub to render persistent indicators on
 *                            job cards without re-querying per render.
 *
 * Time-overlap rule: [aStart, aEnd) overlaps [bStart, bEnd) when
 *   aStart < bEnd AND bStart < aEnd
 * A null start is treated as "all-day" and conflicts with anything on the
 * same date; a null end defaults to start + 60 minutes (matches the
 * NewJobModal default duration). Terminal statuses (complete/cancelled) and
 * hold statuses are filtered out — terminal jobs are history, held jobs are
 * paused work that does not occupy a dispatch slot.
 */

// Statuses that do not generate scheduling conflicts: terminal (history) +
// held (work is paused; the slot is free for other work). Built once so the
// Postgrest IN-list and the in-memory predicate stay in lockstep.
const NON_DISPATCH_STATUSES = [...TERMINAL_STATUSES, ...HOLD_STATUSES]
const NON_DISPATCH_PG_LIST = `(${NON_DISPATCH_STATUSES.join(',')})`

export interface ConflictingJob {
  id: string
  job_number: string | null
  title: string | null
  scheduled_date: string
  scheduled_start: string | null
  scheduled_end: string | null
  contact_name: string | null
}

export interface DetectConflictsInput {
  workspaceId: string
  assigneeType: 'employee' | 'vendor'
  assigneeId: string
  date: string
  start: string | null
  end: string | null
  excludeJobId?: string
}

export async function detectConflicts(input: DetectConflictsInput): Promise<ConflictingJob[]> {
  if (!input.workspaceId || !input.assigneeId || !input.date) return []

  const filterColumn = input.assigneeType === 'employee' ? 'assigned_agent_id' : 'vendor_id'

  let query = supabase
    .from('field_jobs')
    .select('id, job_number, title, scheduled_date, scheduled_start, scheduled_end, contact_name, status')
    .eq('workspace_id', input.workspaceId)
    .eq(filterColumn, input.assigneeId)
    .eq('scheduled_date', input.date)
    .not('status', 'in', NON_DISPATCH_PG_LIST)

  if (input.excludeJobId) {
    query = query.neq('id', input.excludeJobId)
  }

  const { data, error } = await query
  if (error || !data) return []

  return data
    .filter(job => timeOverlaps(
      { start: input.start, end: input.end },
      { start: job.scheduled_start, end: job.scheduled_end },
    ))
    .map(job => ({
      id: job.id,
      job_number: job.job_number,
      title: job.title,
      scheduled_date: job.scheduled_date,
      scheduled_start: job.scheduled_start,
      scheduled_end: job.scheduled_end,
      contact_name: job.contact_name,
    }))
}

interface JobForBulk {
  id: string
  assigned_agent_id: string | null
  vendor_id: string | null
  scheduled_date: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  status: string | null
}

export function detectConflictsInList(jobs: JobForBulk[]): Map<string, string[]> {
  const conflicts = new Map<string, string[]>()
  const active = jobs.filter(j =>
    j.scheduled_date && !NON_DISPATCH_STATUSES.includes(j.status as any),
  )

  // Bucket by date first to avoid the O(n²) sweep going cross-date.
  const byDate = new Map<string, JobForBulk[]>()
  for (const j of active) {
    const list = byDate.get(j.scheduled_date!) ?? []
    list.push(j)
    byDate.set(j.scheduled_date!, list)
  }

  const link = (aId: string, bId: string) => {
    conflicts.set(aId, [...(conflicts.get(aId) ?? []), bId])
    conflicts.set(bId, [...(conflicts.get(bId) ?? []), aId])
  }

  for (const list of byDate.values()) {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i]
        const b = list[j]

        const sameAgent  = a.assigned_agent_id && a.assigned_agent_id === b.assigned_agent_id
        const sameVendor = a.vendor_id && a.vendor_id === b.vendor_id
        if (!sameAgent && !sameVendor) continue

        if (timeOverlaps(
          { start: a.scheduled_start, end: a.scheduled_end },
          { start: b.scheduled_start, end: b.scheduled_end },
        )) {
          link(a.id, b.id)
        }
      }
    }
  }

  return conflicts
}

function timeOverlaps(
  a: { start: string | null; end: string | null },
  b: { start: string | null; end: string | null },
): boolean {
  if (!a.start || !b.start) return true
  const aStart = toMinutes(a.start)
  const aEnd   = a.end ? toMinutes(a.end) : aStart + 60
  const bStart = toMinutes(b.start)
  const bEnd   = b.end ? toMinutes(b.end) : bStart + 60
  return aStart < bEnd && bStart < aEnd
}

function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}
